import React, { useEffect, useState } from "react";
import styled, { keyframes } from "styled-components";
import { motion } from "framer-motion";
import InterestCard from "../components/InterestCard";
import { client } from "../lib/sanity";
import { Link } from "react-router-dom";
import headshot from "../assets/images/headshot.webp";
import FadeInSection from "../components/FadeInSection";
import resume from "../assets/resume/resume.pdf";
import PhotoGallery from "../components/PhotoGallery";
import { FaBullhorn, FaSitemap, FaCode, FaChess, FaCheck } from "react-icons/fa";
import SocialLinksBar from "../components/SocialLinksBar";

// ── Palette ───────────────────────────────────────────
const CHARCOAL = "#111111";
const GOLD = "#C4A25D";
const GOLD_HOVER = "#B08E48";
const GOLD_LIGHT = "#E8D5AA";
const WARM_WHITE = "#F5F0E8";
const MUTED = "#9E9E8E";

// ── Data ──────────────────────────────────────────────
const queryBlogs = `*[_type == "blogPost"] | order(date desc)[0...3]{
  _id, title, slug { current }, date, excerpt, coverImage{ asset->{url} }
}`;

const queryNews = `*[_type == "mediaFeature"] | order(date desc)[0...3]{
  _id, title, date, source, summary, link, coverImage{ asset->{url} }
}`;

const currentYear = new Date().getFullYear();
const yearsExperience = currentYear - 2009;


const EXPERTISE = [
  {
    icon: FaBullhorn,
    title: "PR & Communications",
    skills: [
      "Media relations and press outreach",
      "Crisis communications planning",
      "Messaging development and brand voice",
      "Government and public affairs communications",
    ],
  },
  {
    icon: FaSitemap,
    title: "Fractional Consulting",
    skills: [
      "Communications audits and gap identification",
      "Campaign planning and coordination",
      "Donor and stakeholder engagement",
      "Helping teams get on the same page, messaging-wise",
    ],
  },
  {
    icon: FaCode,
    title: "Web & Digital",
    skills: [
      "Web development with React and Node.js",
      "Building digital tools for communications teams",
      "Workflow design and process documentation",
      "Brand identity and visual media",
    ],
  },
  {
    icon: FaChess,
    title: "Leadership & Team Development",
    skills: [
      "Mentoring communications staff",
      "Leading through organizational change",
      "Budget planning and resource management",
      "Building team culture around clear communication",
    ],
  },
];

const timelineData = [
  {
    date: "2025 \u2013 Present",
    title: "Public Relations Specialist, E2CCB",
    body: "I handle media relations, write public-facing communications, and work on the behind-the-scenes stuff that keeps E2CCB's messaging consistent and credible.",
  },
  {
    date: "Apr 2024 \u2013 2025",
    title: "Director of Development & Communications, Habitat for Humanity Buffalo",
    body: "I led the strategy behind fundraising efforts, media outreach, and donor engagement across WNY and Erie County. From redesigning the website to crafting stories of homeownership impact, my team built affordable housing narratives that resonate with community and policy leaders alike.",
  },
  {
    date: "Oct 2021 \u2013 Apr 2024",
    title: "Chief of Staff, Buffalo Common Council",
    body: "I supported nine district offices, handled communications and media strategy, and helped coordinate major public initiatives across the city.",
  },
  {
    date: "Aug 2019 \u2013 Oct 2021",
    title: "Deputy Chief of Staff, Buffalo Common Council",
    body: "I bridged operational coordination with community outreach and technological upgrades for public meetings.",
  },
  {
    date: "Jan 2015 \u2013 Aug 2019",
    title: "Director of Communications, Buffalo Common Council",
    body: "I managed media relations, handled crisis communications, and ran campaigns that helped raise the Council's public profile during a busy stretch for the city.",
  },
  {
    date: "2013 \u2013 2018",
    title: "Founder & President, Community Canvases",
    body: "I co-founded a public art nonprofit in Buffalo to connect communities and creatives.",
  },
  {
    date: "Oct 2012 \u2013 Jan 2015",
    title: "Senior Legislative Assistant, Buffalo Common Council",
    body: "I researched and drafted policy communications and helped get information out to the communities that needed it.",
  },
];

const interestItems = [
  {
    icon: "\uD83C\uDFE1",
    title: "Homestead Life",
    description:
      "Working on my 40\u2011acre homestead and wildlife sanctuary in Angola, NY with my wife Tracy and our dogs, pigs & chickens.",
  },
  {
    icon: "\uD83C\uDF1E",
    title: "Buffalo Summers",
    description:
      "I love motorcycling, sailing, hiking, gardening, going to the beach, and other adventures as much as Buffalo weather allows.",
  },
  {
    icon: "\uD83C\uDFB6",
    title: "Love + Music",
    description:
      "I love listening to and playing music. I play guitar and piano, and I\u2019ve DJed and performed at 300+ weddings for over 16 years.",
    link: {
      url: "https://open.spotify.com/user/1229681187?si=ffef4f058b1444a3",
      text: "Check Out My Spotify Playlists",
    },
  },
  {
    icon: "\uD83D\uDCBB",
    title: "Coding For Good",
    description: "Coding creative side\u2011projects and tools at jimmontour.dev.",
    link: { url: "https://jimmontour.dev", text: "View My Recent Projects" },
  },
  {
    icon: "\uD83D\uDCDA",
    title: "Reading Goals",
    description:
      "Trying to accomplish my yearly reading goals every year (spoiler, I fail).",
    link: {
      url: "https://www.goodreads.com/user/show/145218425-jim-montour",
      text: "Goodreads List",
    },
  },
  {
    icon: "\uD83C\uDFAC",
    title: "All Things Film",
    description:
      "I love watching and talking horror, cult, so-bad-they\u2019re-good and even sometimes actually good movies!",
    link: {
      url: "https://letterboxd.com/jimmontour/",
      text: "Check Out My Letterboxd",
    },
  },
];

// ── Animation variants ────────────────────────────────
const containerVariants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12 } },
};

const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

const slideInLeft = {
  hidden: { opacity: 0, x: -40 },
  show: { opacity: 1, x: 0, transition: { duration: 0.55, ease: "easeOut" } },
};

// ── Main About component ──────────────────────────────
export default function About() {
  const [blogs, setBlogs] = useState([]);

  useEffect(() => {
    client.fetch(queryBlogs).then(setBlogs);
    client.fetch(queryNews);
  }, []);

  return (
    <>
      <title>About Jim Montour | PR Specialist, Fractional Consultant &amp; Development Leader</title>
      <meta
        name="description"
        content={`Over ${yearsExperience}+ years building PR systems, messaging frameworks, and digital infrastructure for governments, nonprofits, and advocacy organizations.`}
      />
      <meta
        property="og:title"
        content="About Jim Montour | PR Specialist, Fractional Consultant & Development Leader"
      />
      <meta
        property="og:description"
        content="Jim Montour is a PR strategist, fractional consultant, and development leader building communications infrastructure for organizations."
      />
      <link rel="canonical" href="https://jimmontour.com/about" />

      {/* ── Section 1: Dark Hero ── */}
      <HeroSection>
        <HeroText
          as={motion.div}
          variants={containerVariants}
          initial="hidden"
          animate="show"
        >
          <motion.div variants={fadeUp}>
            <HeroOverline>PR Specialist · Fractional Consultant · Development Leader</HeroOverline>
          </motion.div>
          <motion.div variants={fadeUp}>
            <HeroName>Jim Montour</HeroName>
          </motion.div>
          <motion.div variants={fadeUp}>
            <HeroBio>
              I started in communications and never really stopped building things alongside it.
              Over {yearsExperience}+ years, I&apos;ve worked inside government, nonprofits, and
              advocacy organizations &mdash; helping teams get clearer on what they want to say,
              building the processes that keep messaging consistent, and doing the kind of work
              that doesn&apos;t always make the press release but makes everything else possible.
            </HeroBio>
          </motion.div>
          <motion.div variants={fadeUp}>
            <HeroBio>
              These days I work as a PR Specialist at E2CCB, take on consulting work with
              organizations that need communications support but aren&apos;t ready to hire
              full-time, and help teams put better processes in place so their communications
              hold up over time — not just for the next campaign.
            </HeroBio>
          </motion.div>
          <motion.div variants={fadeUp}>
            <HeroBtnRow>
              <HeroPrimaryBtn href={resume} target="_blank" rel="noopener noreferrer">
                View Resume
              </HeroPrimaryBtn>
              <HeroGhostBtn as={Link} to="/contact">
                Work With Me
              </HeroGhostBtn>
            </HeroBtnRow>
          </motion.div>
        </HeroText>

        <HeroPhoto
          as={motion.div}
          initial={{ opacity: 0, scale: 1.04 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.1, ease: "easeOut", delay: 0.2 }}
        >
          <img src={headshot} alt="Jim Montour" />
          <HeroPhotoOverlay />
        </HeroPhoto>
      </HeroSection>

      {/* ── Section 2: Core Expertise ── */}
      <LightSection>
        <InnerWrap>
          <SectionOverline>What I Do</SectionOverline>
          <SectionHeading>Core Expertise</SectionHeading>
          <ExpertiseGrid
            as={motion.div}
            variants={containerVariants}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
          >
            {EXPERTISE.map(({ icon: Icon, title, skills }) => (
              <ExpertiseCard key={title} as={motion.div} variants={fadeUp}>
                <ExpertiseIconWrap>
                  <Icon />
                </ExpertiseIconWrap>
                <ExpertiseTitle>{title}</ExpertiseTitle>
                <ExpertiseSkillList>
                  {skills.map((skill) => (
                    <ExpertiseSkillItem key={skill}>
                      <FaCheck />
                      <span>{skill}</span>
                    </ExpertiseSkillItem>
                  ))}
                </ExpertiseSkillList>
              </ExpertiseCard>
            ))}
          </ExpertiseGrid>
        </InnerWrap>
      </LightSection>

      {/* ── Section 4: Professional Timeline ── */}
      <DarkSection>
        <InnerWrap>
          <SectionOverline>Career Arc</SectionOverline>
          <SectionHeading $light>Professional Timeline</SectionHeading>
          <TimelineList
            as={motion.div}
            variants={containerVariants}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-60px" }}
          >
            {timelineData.map(({ date, title, body }) => (
              <TimelineItem key={title} as={motion.div} variants={slideInLeft}>
                <TimelineDotCol>
                  <TimelineDot />
                  <TimelineLine />
                </TimelineDotCol>
                <TimelineContent>
                  <TimelineDate>{date}</TimelineDate>
                  <TimelineTitle>{title}</TimelineTitle>
                  <TimelineDetail>{body}</TimelineDetail>
                </TimelineContent>
              </TimelineItem>
            ))}
          </TimelineList>
        </InnerWrap>
      </DarkSection>

      {/* ── Section 5: Interests ── */}
      <LightSection>
        <InnerWrap>
          <SectionOverline>Outside the Office</SectionOverline>
          <SectionHeading>Interests &amp; Life</SectionHeading>
          <InterestsGrid
            as={motion.div}
            variants={containerVariants}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-60px" }}
          >
            {interestItems.map((item) => (
              <motion.div key={item.title} variants={fadeUp}>
                <InterestCard
                  icon={item.icon}
                  title={item.title}
                  description={item.description}
                  link={item.link}
                />
              </motion.div>
            ))}
          </InterestsGrid>
        </InnerWrap>
      </LightSection>

      {/* ── Section 6: Photo Gallery ── */}
      <FadeInSection>
        <PhotoGallery />
      </FadeInSection>

      {/* ── Section 7: Blog Posts ── */}
      <LightSection>
        <InnerWrap>
          <FadeInSection>
            <SectionOverline>From the Blog</SectionOverline>
            <SectionHeading>Latest Writing</SectionHeading>
            <BlogGrid>
              {blogs.map((post) => (
                <BlogCard key={post._id} to={`/blog/${post.slug.current}`}>
                  {post.coverImage?.asset?.url && (
                    <BlogCardImageWrap>
                      <img src={post.coverImage.asset.url} alt={post.title} />
                    </BlogCardImageWrap>
                  )}
                  <BlogCardBody>
                    <BlogCardTitle>{post.title}</BlogCardTitle>
                    <BlogCardDate>
                      {new Date(post.date).toLocaleDateString(undefined, {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}
                    </BlogCardDate>
                    <BlogCardExcerpt>{post.excerpt}</BlogCardExcerpt>
                    <span className="read-link">Read More &rarr;</span>
                  </BlogCardBody>
                </BlogCard>
              ))}
            </BlogGrid>
            <CenteredViewAll to="/blog">View All Articles &rarr;</CenteredViewAll>
          </FadeInSection>
        </InnerWrap>
      </LightSection>

      {/* ── Section 8: Connect ── */}
      <SocialLinksBar />
    </>
  );
}

// ── Styled Components ─────────────────────────────────

// Hero
const grain = keyframes`
  0%, 100% { transform: translate(0, 0); }
  10%       { transform: translate(-2%, -3%); }
  30%       { transform: translate(2%, 2%); }
  50%       { transform: translate(-1%, 3%); }
  70%       { transform: translate(3%, -1%); }
  90%       { transform: translate(-2%, 1%); }
`;

const HeroSection = styled.section`
  display: flex;
  min-height: 92vh;
  width: 100%;
  overflow: hidden;
  position: relative;

  @media (max-width: 900px) {
    flex-direction: column;
    min-height: auto;
  }
`;

const HeroText = styled.div`
  flex: 0 0 56%;
  background: ${CHARCOAL};
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 1.5rem;
  padding: 5rem 5rem 5rem 6vw;
  position: relative;
  overflow: hidden;

  &::after {
    content: "";
    position: absolute;
    inset: -50%;
    width: 200%;
    height: 200%;
    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='1'/%3E%3C/svg%3E");
    opacity: 0.025;
    animation: ${grain} 8s steps(1) infinite;
    pointer-events: none;
    z-index: 0;
  }

  > * {
    position: relative;
    z-index: 1;
  }

  @media (max-width: 900px) {
    flex: none;
    padding: 3rem 1.25rem 2.5rem;
    gap: 1rem;
  }

  @media (max-width: 480px) {
    padding: 2.5rem 1rem 2rem;
  }
`;

const HeroOverline = styled.span`
  font-family: 'Inter', sans-serif;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${GOLD};
  opacity: 0.85;
`;

const HeroName = styled.h1`
  font-family: 'DM Serif Display', Georgia, serif;
  font-weight: 400;
  font-size: clamp(3rem, 6vw, 5rem);
  line-height: 1.05;
  color: ${WARM_WHITE};
  letter-spacing: -0.02em;
  margin: 0;
`;

const HeroBio = styled.p`
  font-family: 'Inter', sans-serif;
  font-size: clamp(0.9rem, 1.4vw, 1rem);
  line-height: 1.75;
  color: ${MUTED};
  margin: 0;
  max-width: 520px;
`;

const HeroBtnRow = styled.div`
  display: flex;
  gap: 0.875rem;
  flex-wrap: wrap;
  margin-top: 0.5rem;
`;

const HeroPrimaryBtn = styled.a`
  font-family: 'Inter', sans-serif;
  font-size: 0.9rem;
  font-weight: 600;
  padding: 0.8rem 1.75rem;
  background: ${GOLD};
  color: ${CHARCOAL};
  border-radius: 0.375rem;
  text-decoration: none;
  letter-spacing: 0.01em;
  transition: background 0.2s, transform 0.2s;

  &:hover {
    background: ${GOLD_HOVER};
    color: ${CHARCOAL};
    transform: translateY(-2px);
  }
`;

const HeroGhostBtn = styled.a`
  font-family: 'Inter', sans-serif;
  font-size: 0.9rem;
  font-weight: 600;
  padding: 0.8rem 1.75rem;
  background: transparent;
  color: ${WARM_WHITE};
  border: 1.5px solid rgba(255, 255, 255, 0.25);
  border-radius: 0.375rem;
  text-decoration: none;
  letter-spacing: 0.01em;
  transition: border-color 0.2s, color 0.2s, transform 0.2s;

  &:hover {
    border-color: ${GOLD};
    color: ${GOLD};
    transform: translateY(-2px);
  }
`;

const HeroPhoto = styled.div`
  flex: 1;
  position: relative;
  overflow: hidden;
  min-height: 420px;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: top center;
    display: block;
    filter: grayscale(25%);
    transition: filter 0.4s ease;
  }

  &:hover img {
    filter: grayscale(0%);
  }

  @media (max-width: 900px) {
    height: 55vw;
    max-height: 480px;
    min-height: 260px;
  }
`;

const HeroPhotoOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to right,
    ${CHARCOAL}44 0%,
    transparent 28%
  );
  pointer-events: none;
`;

// Stats Strip
// Section wrappers
const LightSection = styled.section`
  background: #ffffff;
  padding: 5rem 2rem;
  width: 100%;
`;

const DarkSection = styled.section`
  background: ${CHARCOAL};
  padding: 5rem 2rem;
  width: 100%;
`;

const InnerWrap = styled.div`
  max-width: 1100px;
  margin: 0 auto;
`;

const SectionOverline = styled.span`
  font-family: 'Inter', sans-serif;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${GOLD};
  display: block;
  margin-bottom: 0.6rem;
`;

const SectionHeading = styled.h2`
  font-family: 'DM Serif Display', Georgia, serif;
  font-weight: 400;
  font-size: clamp(1.75rem, 3.5vw, 2.5rem);
  color: ${({ $light }) => ($light ? WARM_WHITE : CHARCOAL)};
  margin: 0 0 2.5rem;
  letter-spacing: -0.02em;
`;

// Expertise
const ExpertiseGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const ExpertiseCard = styled.div`
  background: #fafafa;
  border: 1px solid #f0ede6;
  border-radius: 0.75rem;
  padding: 2rem 1.75rem;
  transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 16px 40px rgba(0, 0, 0, 0.07);
    border-color: ${GOLD_LIGHT};
  }
`;

const ExpertiseIconWrap = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: ${GOLD};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
  color: ${CHARCOAL};
  font-size: 1.1rem;
`;

const ExpertiseTitle = styled.h3`
  font-family: 'DM Serif Display', Georgia, serif;
  font-weight: 400;
  font-size: 1.2rem;
  color: ${CHARCOAL};
  margin: 0 0 1rem;
  line-height: 1.3;
`;

const ExpertiseSkillList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
`;

const ExpertiseSkillItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 0.625rem;
  font-family: 'Inter', sans-serif;
  font-size: 0.875rem;
  color: #4b5563;
  line-height: 1.55;

  svg {
    color: ${GOLD};
    font-size: 0.75rem;
    flex-shrink: 0;
    margin-top: 0.25rem;
  }
`;

// Timeline
const TimelineList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const TimelineItem = styled.div`
  display: flex;
  gap: 1.5rem;
  align-items: flex-start;
`;

const TimelineDotCol = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
  padding-top: 0.25rem;
`;

const TimelineDot = styled.div`
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: ${GOLD};
  border: 2px solid ${GOLD};
  flex-shrink: 0;
  position: relative;
  z-index: 1;
`;

const TimelineLine = styled.div`
  width: 2px;
  flex: 1;
  min-height: 40px;
  background: ${GOLD}44;
  margin-top: 4px;

  ${TimelineItem}:last-child & {
    display: none;
  }
`;

const TimelineContent = styled.div`
  padding-bottom: 2.5rem;
  flex: 1;
`;

const TimelineDate = styled.span`
  font-family: 'Inter', sans-serif;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${GOLD};
  display: block;
  margin-bottom: 0.4rem;
`;

const TimelineTitle = styled.h3`
  font-family: 'DM Serif Display', Georgia, serif;
  font-weight: 400;
  font-size: 1.15rem;
  color: ${WARM_WHITE};
  margin: 0 0 0.6rem;
  line-height: 1.35;
`;

const TimelineDetail = styled.div`
  font-family: 'Inter', sans-serif;
  font-size: 0.9rem;
  color: ${MUTED};
  line-height: 1.75;
`;

// Interests
const InterestsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(280px, 100%), 1fr));
  gap: 1.25rem;
`;

// Blog
const BlogGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(300px, 100%), 1fr));
  gap: 2rem;
  margin-top: 1rem;
`;

const BlogCard = styled(Link)`
  display: flex;
  flex-direction: column;
  background: white;
  border-radius: 0.75rem;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  border: 1px solid #f3f4f6;
  text-decoration: none;
  color: inherit;
  transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 16px 40px rgba(0, 0, 0, 0.1);
    border-color: ${GOLD_LIGHT};
  }

  &:hover img {
    transform: scale(1.05);
  }

  .read-link {
    display: block;
    margin: auto 1rem 1rem;
    font-weight: 600;
    color: ${GOLD};
    font-size: 0.825rem;
    font-family: 'Inter', sans-serif;
  }
`;

const BlogCardImageWrap = styled.div`
  overflow: hidden;
  height: 180px;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    transition: transform 0.5s ease;
  }
`;

const BlogCardBody = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: 0;
`;

const BlogCardTitle = styled.h3`
  font-family: 'DM Serif Display', Georgia, serif;
  font-weight: 400;
  font-size: 1.05rem;
  margin: 0.75rem 1rem 0;
  color: #111827;
  line-height: 1.35;
`;

const BlogCardDate = styled.span`
  font-size: 0.8rem;
  color: #9ca3af;
  margin: 0.3rem 1rem;
  font-family: 'Inter', sans-serif;
  display: block;
`;

const BlogCardExcerpt = styled.div`
  margin: 0.25rem 1rem 0.75rem;
  font-size: 0.875rem;
  color: #4b5563;
  line-height: 1.65;
  font-family: 'Inter', sans-serif;
`;

const CenteredViewAll = styled(Link)`
  display: block;
  text-align: center;
  margin-top: 2.5rem;
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  font-size: 0.9rem;
  color: ${GOLD};
  text-decoration: none;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.75;
  }
`;
